import { useState, useEffect, useCallback, useRef } from 'react';
import { WorkflowDashboardData } from '../../types/workflow';
import { workflowService } from '../../services/workflowService';

/**
 * Return type for the useWorkflowStats hook.
 * Provides workflow dashboard statistics with loading and error states.
 */
export interface UseWorkflowStatsReturn {
  /** The workflow dashboard statistics data, null if not yet loaded */
  stats: WorkflowDashboardData | null;
  /** Whether the stats are currently being fetched */
  loading: boolean;
  /** Error message if fetch failed, null otherwise */
  error: string | null;
  /** Function to manually refetch the stats */
  refetch: () => void;
}

/**
 * Hook for fetching workflow dashboard statistics.
 *
 * Single Responsibility: ONLY handles stats fetching.
 * Does not manage filters, selection, or other state.
 *
 * @returns Object containing stats, loading state, error state, and refetch function
 *
 * @example
 * ```tsx
 * function WorkflowDashboard() {
 *   const { stats, loading, error, refetch } = useWorkflowStats();
 *
 *   if (loading) return <LoadingSpinner />;
 *   if (error) return <ErrorMessage message={error} />;
 *
 *   return (
 *     <div>
 *       <h2>Total Active: {stats?.totalActiveBatches}</h2>
 *       <h2>Overdue: {stats?.overdueBatches}</h2>
 *       <button onClick={refetch}>Refresh</button>
 *     </div>
 *   );
 * }
 * ```
 */
export function useWorkflowStats(): UseWorkflowStatsReturn {
  const [stats, setStats] = useState<WorkflowDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Track current request to prevent stale updates
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchStats = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setError(null);

    try {
      const data = await workflowService.getDashboard(signal);

      // Only update state if request wasn't aborted
      // This prevents stale data from overwriting newer data
      if (!signal?.aborted) {
        setStats(data);
      }
    } catch (err: unknown) {
      // Ignore abort errors - they're intentional when component unmounts
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }
      // Also check for axios CanceledError which wraps AbortError
      if (err instanceof Error && err.name === 'CanceledError') {
        return;
      }

      // Only update error state if request wasn't aborted
      if (!signal?.aborted) {
        const errorMessage = err instanceof Error
          ? err.message
          : 'Failed to fetch workflow stats';
        setError(errorMessage);
      }
    } finally {
      // Only update loading state if request wasn't aborted
      if (!signal?.aborted) {
        setLoading(false);
      }
    }
  }, []);

  // Effect with request cancellation
  useEffect(() => {
    // Cancel any in-flight request
    abortControllerRef.current?.abort();

    // Create new controller for this request
    const controller = new AbortController();
    abortControllerRef.current = controller;

    // eslint-disable-next-line react-hooks/set-state-in-effect -- data-fetch effect intentionally sets loading state synchronously at request start
    fetchStats(controller.signal);

    // Cleanup: cancel request if component unmounts
    return () => controller.abort();
  }, [fetchStats]);

  /**
   * Manual refetch function - user initiated.
   * Cancels any in-flight request and starts a new one.
   */
  const refetch = useCallback(() => {
    // Cancel any existing request
    abortControllerRef.current?.abort();

    // Create new controller for the refetch
    const controller = new AbortController();
    abortControllerRef.current = controller;

    fetchStats(controller.signal);
  }, [fetchStats]);

  return {
    stats,
    loading,
    error,
    refetch
  };
}
